import { Loader2, RefreshCw, Upload } from "lucide-react";
import { InlineWarning, Panel, StatusBadge } from "@/components/common/Primitives";
import { dateTime, num } from "@/lib/format";
import { SAMPLE } from "@/components/integrations/constants";

const inputCls =
  "h-8 w-full rounded-sm border border-slate-300 bg-white px-2 text-[12.5px] outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200";

const Field = ({ label, hint, children }) => (
  <div>
    <label className="label-xs">{label}</label>
    <div className="mt-1">{children}</div>
    {hint && <p className="mt-0.5 text-[10.5px] text-slate-500">{hint}</p>}
  </div>
);

const CATALOGUE_METRICS = [
  ["Merken", "makes"],
  ["Modellen", "models"],
  ["Generaties", "generations"],
  ["Uitvoeringen", "variants"],
];

export const StatusTab = ({ catalogue, data, busy, onRefreshCatalogue, onTest }) => (
  <div className="space-y-2">
    <Panel
      title="Voertuigcatalogus"
      subtitle="Merken, modellen, generaties en uitvoeringen voor de scanselector"
      testid="vehicle-catalogue-panel"
      actions={
        <button
          type="button"
          onClick={onRefreshCatalogue}
          disabled={busy}
          data-testid="refresh-catalogue-button"
          className="flex items-center gap-1.5 rounded-sm bg-blue-600 px-2.5 py-1.5 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Voertuigcatalogus bijwerken
        </button>
      }
    >
      <div className="grid grid-cols-2 gap-2 p-3 md:grid-cols-5">
        {CATALOGUE_METRICS.map(([label, key]) => (
          <div key={label} className="rounded-sm border border-slate-200 px-2.5 py-2">
            <div className="label-xs">{label}</div>
            <div className="mono mt-0.5 text-[15px] font-semibold text-slate-900">
              {catalogue?.[key] === undefined || catalogue?.[key] === null ? "—" : num(catalogue[key])}
            </div>
          </div>
        ))}
        <div className="rounded-sm border border-slate-200 px-2.5 py-2">
          <div className="label-xs">Laatst bijgewerkt</div>
          <div className="mt-0.5 text-[11.5px] text-slate-700">
            {catalogue?.last_updated ? dateTime(catalogue.last_updated) : "—"}
          </div>
        </div>
      </div>
      <div className="divide-y divide-slate-100 border-t border-slate-100">
        {(catalogue?.sources || []).map((s) => (
          <div key={s.key} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2" data-testid={`catalogue-source-${s.key}`}>
            <div className="min-w-0">
              <div className="text-[12.5px] font-semibold text-slate-800">
                {s.priority}. {s.label}
              </div>
              <div className="text-[11px] text-slate-500">{s.message}</div>
            </div>
            <StatusBadge status={s.status} label={s.status === "CONNECTED" ? "Actief" : undefined} />
          </div>
        ))}
        <p className="px-3 py-2 text-[11px] text-slate-500">
          Modellen, generaties en uitvoeringen worden per merk/model bijgewerkt zodra ze voor het
          eerst worden opgevraagd, en daarna {catalogue?.ttl_days ?? 30} dagen hergebruikt uit de cache.
        </p>
      </div>
    </Panel>

    <div className="grid gap-2 lg:grid-cols-2">
      {(data?.providers || []).map((p) => (
        <Panel key={p.key} title={p.label} testid={`integration-${p.key}`}>
          <div className="space-y-2 p-3">
            <div className="flex items-center justify-between gap-2">
              <StatusBadge status={p.status} label={p.status_label} testid={`integration-status-${p.key}`} />
              <span className="text-[10.5px] text-slate-400">{(p.countries || []).join(", ") || "—"}</span>
            </div>
            <p className="text-[12px] leading-snug text-slate-600">{p.description}</p>
            {p.message && <p className="text-[11.5px] text-slate-500">{p.message}</p>}
            {(p.required_credentials || []).length > 0 && (
              <div>
                <div className="label-xs">Benodigde credentials</div>
                <ul className="mt-1 list-inside list-disc text-[11px] text-slate-600">
                  {p.required_credentials.map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
              </div>
            )}
            {p.enabled && (
              <button
                type="button"
                onClick={() => onTest(p.key)}
                disabled={busy}
                data-testid={`integration-test-${p.key}`}
                className="rounded-sm border border-slate-300 px-2.5 py-1 text-[11.5px] font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
              >
                Verbinding testen
              </button>
            )}
          </div>
        </Panel>
      ))}
      {data?.ai && (
        <Panel title={data.ai.label} testid="integration-openai">
          <div className="space-y-2 p-3">
            <StatusBadge status={data.ai.status} label={data.ai.status_label} />
            <p className="text-[12px] text-slate-600">{data.ai.description}</p>
            <div className="mono text-[11.5px] text-slate-700">
              Model: {data.ai.model} ({data.ai.provider}) · {data.ai.enabled ? "actief" : "uitgeschakeld"}
            </div>
          </div>
        </Panel>
      )}
    </div>
  </div>
);

export const RequiredTab = ({ requiredAccess }) => (
  <div className="space-y-2">
    <InlineWarning tone="danger">
      <b>Nog benodigde externe koppelingen</b> — zolang deze ontbreken worden er géén voertuigen
      getoond. Er wordt geen demo- of fictieve data gegenereerd.
    </InlineWarning>
    {(requiredAccess || []).map((item) => (
      <Panel key={item.provider} title={item.provider} testid={`required-${item.provider}`}>
        <div className="space-y-2 p-3 text-[12.5px] leading-snug text-slate-700">
          <div>
            <span className="label-xs">Wat is nodig</span>
            <p>{item.need}</p>
          </div>
          <div>
            <span className="label-xs">Waarom</span>
            <p className="text-slate-600">{item.why}</p>
          </div>
          <div>
            <span className="label-xs">Hoe aanvragen</span>
            <p className="text-slate-600">{item.how}</p>
          </div>
          <div>
            <span className="label-xs">Omgevingsvariabelen (server-side)</span>
            <div className="mt-1 flex flex-wrap gap-1">
              {item.env_keys.map((k) => (
                <code key={k} className="mono rounded-sm border border-slate-300 bg-slate-50 px-1.5 py-0.5 text-[11px]">
                  {k}
                </code>
              ))}
            </div>
          </div>
        </div>
      </Panel>
    ))}
  </div>
);

export const IngestTab = ({ form, setForm, json, setJson, busy, onSubmitForm, onSubmitJson }) => {
  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  return (
    <div className="grid gap-2 xl:grid-cols-2">
      <Panel
        title="Echte advertentie handmatig invoeren"
        subtitle="Gebruik uitsluitend werkelijke advertentiegegevens met de exacte advertentie-URL"
        testid="ingest-form-panel"
      >
        <div className="grid gap-2.5 p-3 md:grid-cols-2">
          <Field label="Advertentie-ID (bron)" hint="Verplicht — uniek per bron">
            <input value={form.external_id} data-testid="ingest-external-id" onChange={(e) => set("external_id", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Directe advertentie-URL" hint="Verplicht — nooit een zoekresultaatpagina">
            <input value={form.url} data-testid="ingest-url" onChange={(e) => set("url", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Land">
            <select value={form.country} data-testid="ingest-country" onChange={(e) => set("country", e.target.value)} className={inputCls}>
              {["DE", "NL", "BE", "AT", "IT", "FR", "ES"].map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </Field>
          <Field label="Rol" hint="SOURCE = inkoopkandidaat, COMPARABLE = NL vergelijkingsauto">
            <select value={form.role} data-testid="ingest-role" onChange={(e) => set("role", e.target.value)} className={inputCls}>
              <option value="SOURCE">SOURCE</option>
              <option value="COMPARABLE">COMPARABLE</option>
            </select>
          </Field>
          <Field label="Merk">
            <input value={form.make} data-testid="ingest-make" onChange={(e) => set("make", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Model">
            <input value={form.model} data-testid="ingest-model" onChange={(e) => set("model", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Generatie">
            <input value={form.generation} onChange={(e) => set("generation", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Uitvoering">
            <input value={form.variant} data-testid="ingest-variant" onChange={(e) => set("variant", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Vraagprijs (€)">
            <input type="number" value={form.price} data-testid="ingest-price" onChange={(e) => set("price", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Kilometerstand">
            <input type="number" value={form.mileage_km} data-testid="ingest-mileage" onChange={(e) => set("mileage_km", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Eerste toelating (JJJJ-MM)">
            <input value={form.first_registration} data-testid="ingest-first-registration" onChange={(e) => set("first_registration", e.target.value)} className={inputCls} placeholder="2021-06" />
          </Field>
          <Field label="CO₂ WLTP (g/km)" hint="Leeg laten als onbekend — nooit schatten">
            <input type="number" value={form.co2_wltp} data-testid="ingest-co2" onChange={(e) => set("co2_wltp", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Brandstof">
            <input value={form.fuel} onChange={(e) => set("fuel", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Transmissie">
            <input value={form.transmission} onChange={(e) => set("transmission", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Vermogen (pk)">
            <input type="number" value={form.power_hp} onChange={(e) => set("power_hp", e.target.value)} className={inputCls} />
          </Field>
          <Field label="BTW-status">
            <select value={form.vat_status} data-testid="ingest-vat-status" onChange={(e) => set("vat_status", e.target.value)} className={inputCls}>
              {["BTW aftrekbaar", "Marge-auto", "BTW niet aftrekbaar", "Onbekend"].map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </Field>
          <Field label="Verkopertype">
            <select value={form.seller_type} onChange={(e) => set("seller_type", e.target.value)} className={inputCls}>
              <option value="Dealer">Dealer</option>
              <option value="Particulier">Particulier</option>
            </select>
          </Field>
          <Field label="Verkopernaam">
            <input value={form.seller_name} onChange={(e) => set("seller_name", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Plaats">
            <input value={form.city} onChange={(e) => set("city", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Kleur">
            <input value={form.colour} onChange={(e) => set("colour", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Carrosserie">
            <input value={form.body_type} onChange={(e) => set("body_type", e.target.value)} className={inputCls} />
          </Field>
          <Field label="VIN">
            <input value={form.vin} onChange={(e) => set("vin", e.target.value)} className={inputCls} />
          </Field>
          <div className="md:col-span-2">
            <Field label="Foto-URL's" hint="Eén per regel of komma-gescheiden">
              <textarea value={form.image_urls} onChange={(e) => set("image_urls", e.target.value)} className="h-16 w-full rounded-sm border border-slate-300 px-2 py-1 text-[12px]" />
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field label="Advertentietekst" hint="Gebruikt voor risicodetectie met bronbewijs">
              <textarea value={form.listing_text} data-testid="ingest-listing-text" onChange={(e) => set("listing_text", e.target.value)} className="h-24 w-full rounded-sm border border-slate-300 px-2 py-1 text-[12px]" />
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field label="Opties / uitrusting" hint="Eén per regel of komma-gescheiden">
              <textarea value={form.equipment} onChange={(e) => set("equipment", e.target.value)} className="h-16 w-full rounded-sm border border-slate-300 px-2 py-1 text-[12px]" />
            </Field>
          </div>
        </div>
        <div className="border-t border-slate-100 px-3 py-2">
          <button
            type="button"
            onClick={onSubmitForm}
            disabled={busy || !form.external_id || !form.url || !form.make || !form.model || form.price === ""}
            data-testid="ingest-submit"
            className="flex items-center gap-1.5 rounded-sm bg-blue-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            Advertentie opslaan en analyseren
          </button>
        </div>
      </Panel>

      <Panel title="JSON-ingestie" subtitle="Bulk import van echte advertentiedata" testid="ingest-json-panel">
        <div className="p-3">
          <InlineWarning tone="info">
            Plak een array met echte advertenties. Ontbrekende velden blijven leeg — er worden nooit
            waarden verzonnen. Verplicht: <code>external_id</code>, <code>url</code>,{" "}
            <code>country</code>, <code>make</code>, <code>model</code>, <code>price</code>.
          </InlineWarning>
          <textarea
            value={json}
            data-testid="ingest-json-textarea"
            onChange={(e) => setJson(e.target.value)}
            placeholder={SAMPLE}
            className="mono mt-3 h-80 w-full rounded-sm border border-slate-300 px-2 py-1.5 text-[11.5px]"
          />
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={onSubmitJson}
              disabled={busy || !json.trim()}
              data-testid="ingest-json-submit"
              className="rounded-sm bg-blue-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              JSON importeren
            </button>
            <button
              type="button"
              onClick={() => setJson(SAMPLE)}
              className="rounded-sm border border-slate-300 px-3 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-50"
            >
              Voorbeeldstructuur invullen
            </button>
          </div>
        </div>
      </Panel>
    </div>
  );
};

export const LogsTab = ({ logs }) => (
  <Panel title="API-logboek" subtitle="Provider-aanroepen, responstijd en resultaten. Geen sleutels gelogd." testid="api-logs-panel">
    {(logs || []).length === 0 ? (
      <p className="px-3 py-4 text-[12px] text-slate-500">Nog geen provider-aanroepen gelogd.</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th>Tijdstip</th>
              <th>Provider</th>
              <th>Actie</th>
              <th>Resultaat</th>
              <th>Responstijd</th>
              <th>Records</th>
              <th>Bericht</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <tr key={log.id}>
                <td className="num">{dateTime(log.timestamp)}</td>
                <td className="text-[12px] font-semibold">{log.provider}</td>
                <td className="text-[12px]">{log.action}</td>
                <td>
                  {log.success ? (
                    <span className="chip border-emerald-300 bg-emerald-50 text-emerald-800">OK</span>
                  ) : (
                    <span className="chip border-red-300 bg-red-50 text-red-800">Fout</span>
                  )}
                </td>
                <td className="num">{num(log.duration_ms)} ms</td>
                <td className="num">{num(log.records_returned ?? 0)}</td>
                <td className="max-w-[320px] truncate text-[11.5px] text-slate-500">{log.message || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </Panel>
);
